import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChhattisgarhGnaTgna } from "../../../models/rajasthan";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { FUNCTIONS } from "../../Hppc/const_hppc";
import { H_CHART_TYPES } from "../../Hppc/const";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { SearchBox } from "../../../components/Search";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import { ExchangeColors } from "../Exchange3/FormatData";
import { AddDatesWithFileName } from "../../../helpers/helper";
import {
  getChattisgarhGnaTgnaData,
  getChattisgarhIsgsData,
  getChattisgarhOaRemcData,
  getChhattisgarhStateAvailability,
  getCSPDCLGenerationAnalyticsMetrics,
  type CSPDCLGenerationAnalyticsMetrics,
} from "../Utility/RestApi";
import { MetricChipHead } from "../../../components/Metrics";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

const GROUP_BY = [
  {
    label: "State",
  },
  {
    label: "Utility",
  },
  {
    label: "IR Link",
  },
  {
    label: "Trader",
  },
  // {
  //   label: "Generator",
  // },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh"];

const CONTRACT_TYPES = ["Short Term", "Long Term"];

// const PERIPHERY = [
//   {
//     label: "Generator",
//     value: "Generator Periphery",
//   },
//   {
//     label: "State",
//     value: "State Periphery",
//   },
// ];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export function CgsGenOverview() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [allData, setAllData] = useState<{
    gnaTgna: ChhattisgarhGnaTgna[];
    isgs: ChhattisgarhGnaTgna[];
    oaRemc: ChhattisgarhGnaTgna[];
    stateGen: any[];
  }>({ gnaTgna: [], isgs: [], oaRemc: [], stateGen: [] });
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[3]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);
  const [selectedVariable, setSelectedVariable] = useState<string[]>(["Buy"]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    "OA GNA",
    "OA TGNA",
    "ISGS",
    "OA REMC",
  ]);
  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([
    "Long Term",
  ]);
  const [selectedGeneratorType, setSelectedGeneratorType] = useState<string[]>(
    ["State", "Central"],
  );
  const [selectedStateGenTypes, setSelectedStateGenTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedUtilities, setSelectedUtilities] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[0]);
  // const [selectedPeriphery, setSelectedPeriphery] = useState(PERIPHERY[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  // Track which legends are selected in the chart (empty = all selected)
  const [activeLegends, setActiveLegends] = useState<LegendKey[]>([]);
  const [analyticsMetrics, setAnalyticsMetrics] =
    useState<CSPDCLGenerationAnalyticsMetrics | null>(null);

  const processItems = useCallback(
    (items: ChhattisgarhGnaTgna[]): ChhattisgarhGnaTgna[] =>
      items.map((item) => ({
        ...item,
        slot: Number(item.slot),
        buy_sell: item.type === "Buyer" ? "Buy" : "Sell",
        final_amount_mwh: Math.abs(item.final_amount) / 4,
        final_amount_mw: Math.abs(item.final_amount),
        schd_amount_mwh: Math.abs(item.schd_amount) / 4,
        schd_amount_mw: Math.abs(item.schd_amount),
        month: getMonthFromDate(item.date),
      })),
    [],
  );

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates["Overview"]?.defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates["Overview"].defaultRangeDate;
    const startDate = convertToString(dates.startDate);
    const endDate = convertToString(dates.endDate);

    const [gnaTgna, isgs, oaRemc, stateGenRaw] = await Promise.all([
      getChattisgarhGnaTgnaData({ startDate, endDate }),
      getChattisgarhIsgsData({ startDate, endDate }),
      getChattisgarhOaRemcData({ startDate, endDate }),
      getChhattisgarhStateAvailability({ startDate, endDate }),
    ]);

    const stateGen = stateGenRaw.map((item) => ({
      ...item,
      slot: Number(item.slot),
      category: "State Generation",
      buy_sell: item.generator_type_name,
      final_amount_mw: item.value ?? 0,
      final_amount_mwh: (item.value ?? 0) / 4,
      schd_amount_mw: 0,
      schd_amount_mwh: 0,
      month: getMonthFromDate(item.date),
      type: "Buyer",
      contract_type: "",
      trader: "",
      link_name: "",
      seller: "",
      buyer: "",
      seller_parent_state_utility: "",
      buyer_parent_state_utility: "",
    }));

    setAllData({
      gnaTgna: processItems(gnaTgna),
      isgs: processItems(isgs),
      oaRemc: processItems(oaRemc),
      stateGen,
    });
    setLoading(false);
  }, [DefaultDates, processItems]);

  const stateGenTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    allData.stateGen.forEach((item: any) => {
      if (item.generator_type_name) seen.add(item.generator_type_name);
    });
    return Array.from(seen)
      .sort()
      .map((v) => ({ label: v, value: v }));
  }, [allData.stateGen]);

  const utilityOptions = useMemo(() => {
    const seen = new Set<string>();
    [...allData.gnaTgna, ...allData.isgs, ...allData.oaRemc].forEach(
      (item: any) => {
        if (item.seller) seen.add(item.seller);
        if (item.buyer) seen.add(item.buyer);
      },
    );
    return Array.from(seen)
      .sort()
      .map((v) => ({ label: v, value: v }));
  }, [allData]);

  const data = useMemo(() => {
    const includeState = selectedGeneratorType.includes("State");
    // Default (nothing selected) behaves the same as Central-only
    const includeCentral =
      selectedGeneratorType.includes("Central") ||
      selectedGeneratorType.length === 0;

    const dedicated = ["ISGS", "OA REMC"];
    let result: any[] = [];

    if (includeState) {
      const stateFiltered =
        selectedStateGenTypes.length > 0
          ? allData.stateGen.filter((item: any) =>
              selectedStateGenTypes.some(
                (s) => s.value === item.generator_type_name,
              ),
            )
          : allData.stateGen;
      result = result.concat(stateFiltered);
    }

    if (includeCentral) {
      if (selectedCategories.includes("ISGS"))
        result = result.concat(allData.isgs);
      if (selectedCategories.includes("OA REMC"))
        result = result.concat(allData.oaRemc);
      if (selectedCategories.some((c) => !dedicated.includes(c)))
        result = result.concat(allData.gnaTgna);
    }

    return result;
  }, [allData, selectedCategories, selectedGeneratorType, selectedStateGenTypes]);

  const getKeys = useCallback(() => {
    if (selectedKey.key === "time") {
      return ["date", "slot", "slot_with_time"];
    } else if (selectedKey.key === "date") {
      return ["date"];
    } else {
      return [selectedKey.values[0]];
    }
  }, [selectedKey]);

  useEffect(() => {
    let filteredData = data;

    // Block filter — selectedBlock contains objects {value, label} from BlocksSelector
    if (selectedBlock.length > 0) {
      const selectedSlots = new Set(
        selectedBlock.map((b: any) => (b?.value !== undefined ? b.value : b)),
      );
      filteredData = filteredData.filter((item) =>
        selectedSlots.has(item.slot),
      );
    }

    const isStateOnly =
      selectedGeneratorType.includes("State") &&
      !selectedGeneratorType.includes("Central") &&
      selectedGeneratorType.length > 0;
    const isCumulative = !selectedFunction.comparative;

    // Variable filter — state gen shows when State-only or when "Sell" is selected;
    // in Comparative/Cumulative+Both it requires "Sell" to be selected
    if (selectedVariable.length > 0) {
      const typeSet = new Set<string>(
        selectedVariable.map((v) => (v === "Buy" ? "Buyer" : "Seller")),
      );
      filteredData = filteredData.filter((item) => {
        if (item.category === "State Generation")
          return isStateOnly || selectedVariable.includes("Sell");
        return typeSet.has(item.type);
      });
    }

    // Category filter — dedicated endpoint categories are already scoped by the data memo;
    // gnaTgna items (OA GNA / OA TGNA) are further filtered by their category field.
    const dedicatedEndpointCategories = ["ISGS", "OA REMC"];
    const gnaTgnaCategories = ["OA GNA", "OA TGNA"];
    const nonDedicatedSelected = selectedCategories.filter(
      (c) => !dedicatedEndpointCategories.includes(c),
    );
    if (nonDedicatedSelected.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          item.category === "State Generation" ||
          !gnaTgnaCategories.includes(item.category) ||
          nonDedicatedSelected.includes(item.category),
      );
    }

    // Contract type filter — state gen always shows (no contract type applies to it)
    if (selectedContractTypes.length > 0) {
      filteredData = filteredData.filter((item) => {
        if (item.category === "State Generation") return true;
        return selectedContractTypes.includes(item.contract_type);
      });
    }

    // Utility filter — active only when Group By is "Utility"
    if (
      selectedGroupKeys.label === "Utility" &&
      selectedUtilities.length > 0
    ) {
      const utilitySet = new Set(selectedUtilities.map((u) => u.value));
      filteredData = filteredData.filter(
        (item) =>
          item.category === "State Generation" ||
          utilitySet.has(item.seller) ||
          utilitySet.has(item.buyer),
      );
    }

    // Periphery filter (commented out as not available in new API response)
    // filteredData = filteredData.filter(
    //   (item) => item.boundary_type__boundary_type === selectedPeriphery.value,
    // );

    function getGroupByKey() {
      if (selectedGroupKeys.label === "State")
        return ["seller_parent_state_utility", "buyer_parent_state_utility"];
      if (selectedGroupKeys.label === "Utility") return ["seller", "buyer"];
      if (selectedGroupKeys.label === "IR Link") return ["link_name"];
      if (selectedGroupKeys.label === "Trader") return ["trader"];
      if (selectedGroupKeys.label === "Category") return ["category"];
      return [];
    }

    const groupByKeys = selectedFunction.comparative ? getGroupByKey() : [];
    const hasStateGen = selectedGeneratorType.includes("State");
    if (nonDedicatedSelected.length > 0 || hasStateGen || isCumulative) {
      groupByKeys.push("category");
    }
    groupByKeys.push("buy_sell");

    // Create data with slot_with_time for table display.
    // In Cumulative mode, collapse all state gen generator types into a single total series.
    const dataWithSlotTime = filteredData.map((item) => ({
      ...item,
      slot_with_time: item.slot || "-",
      buy_sell:
        isCumulative && item.category === "State Generation"
          ? ""
          : item.buy_sell,
    }));

    const isAvgInterval =
      selectedKey.key === "date" || selectedKey.key === "month";
    const amountKeys = [
      "final_amount_mwh",
      "final_amount_mw",
      "schd_amount_mwh",
      "schd_amount_mw",
    ];

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "slot", "slot_with_time"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: isAvgInterval ? [] : amountKeys,
      averageKeys: isAvgInterval ? amountKeys : [],
      averageDuplicationKeys: ["slot", "date", "slot_with_time"],
      priceKey: "",
      data: dataWithSlotTime,
    };

    const cData = FormatChartData(par);

    // Ensure slot_with_time is properly set in table data
    const processedTableData = (cData.tableData || []).map((row) => ({
      ...row,
      slot_with_time: row.slot_with_time || row.slot || "-",
    }));

    setTableData(processedTableData);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedCategories,
    selectedContractTypes,
    selectedGroupKeys,
    selectedFunction,
    // selectedPeriphery,
    getKeys,
    selectedKey.key,
    selectedBlock,
    selectedVariable,
    selectedUtilities,
    selectedGeneratorType,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    const dates = DefaultDates["Overview"]?.defaultRangeDate;
    if (!dates) return;
    const startDate = convertToString(dates.startDate);
    const endDate = convertToString(dates.endDate);
    const blocks =
      selectedBlock.length > 0
        ? selectedBlock.map((b: any) => (b?.value !== undefined ? b.value : b))
        : undefined;
    getCSPDCLGenerationAnalyticsMetrics({
      ranges: [{ startDate, endDate, ...(blocks ? { blocks } : {}) }],
    }).then(setAnalyticsMetrics);
  }, [DefaultDates, selectedBlock]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? ["slot_with_time", selectedKey.values[1]]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.isTime
    ? [
        "Time Slot",
        selectedKey.values[1] === "date" ? "Date" : selectedKey.values[1],
      ]
    : [selectedKey.values[0] === "date" ? "Date" : selectedKey.values[0]];

  function getActiveLegends() {
    return chartKeys.map((key, index) => {
      let name = key;
      const suffix = selectedUnit === "MWh" ? "_mwh" : "_mw";

      const isStateGenKey = key.includes("State Generation");

      if (
        !isStateGenKey &&
        (selectedGroupKeys.label === "State" ||
          selectedGroupKeys.label === "Utility")
      ) {
        name = name
          .replace("_", " -> ")
          .replaceAll("_", " ")
          .replaceAll(" STATE", "")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
      } else {
        name = name
          .replaceAll("_", " ")
          .replaceAll("null", "Others")
          .replaceAll("undefined", " ")
          .replaceAll(" STATE", "")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
        if (isStateGenKey) name = name.trim() + " (Sell)";
      }

      return {
        dataKey: key + `_final_amount${suffix}`,
        name: name,
        legendColor: undefined,
        stroke: ExchangeColors[index % ExchangeColors.length],
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined
            ? key.includes("Buy")
              ? "buy"
              : "sell"
            : undefined,
      };
    });
  }

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(getActiveLegends());

  const tablePar = {
    legends: visibleLegends,
    data: chartData || [],
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: `Generation ${selectedCategories.join(", ")}`,
      dates: DefaultDates["Overview"]?.defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  // Check if there's no data to display
  const hasNoData =
    !loading && (chartData.length === 0 || chartKeys.length === 0);

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />

          <FilterMenu
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
            title={"Unit"}
          />

          <FilterMenu
            title="Function"
            items={FUNCTIONS.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => {
              setSelectedFunction(FUNCTIONS[index]);
            }}
          />

          {/* <FilterMenu
            title="Periphery"
            items={PERIPHERY.map((item) => item.label)}
            selectedItems={[selectedPeriphery.label]}
            onSelect={(item, index) => {
              setSelectedPeriphery(PERIPHERY[index]);
            }}
          /> */}

          <FilterMenu
            title="Generator Type"
            items={["State", "Central"]}
            selectedItems={selectedGeneratorType}
            onSelect={() => {}}
            setSelectedItems={setSelectedGeneratorType}
          />

          {(selectedGeneratorType.length === 0 ||
            selectedGeneratorType.includes("Central")) && (
            <>
              <FilterMenu
                title="Variable"
                items={["Buy", "Sell"]}
                selectedItems={selectedVariable}
                onSelect={() => {}}
                setSelectedItems={setSelectedVariable}
              />
              <FilterMenu
                title="Contract Type"
                items={CONTRACT_TYPES}
                selectedItems={selectedContractTypes}
                onSelect={() => {}}
                setSelectedItems={setSelectedContractTypes}
              />

              <FilterMenu
                title="Category"
                items={["OA GNA", "OA TGNA", "ISGS", "OA REMC"]}
                selectedItems={selectedCategories}
                onSelect={() => {}}
                setSelectedItems={setSelectedCategories}
              />

              <FilterMenu
                title="Group By"
                items={GROUP_BY.map((item) => item.label)}
                selectedItems={[selectedGroupKeys.label]}
                onSelect={(_item, index) => {
                  setSelectedGroupKeys(GROUP_BY[index]);
                }}
              />
            </>
          )}

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {analyticsMetrics && (
          <div className="flex flex-wrap gap-2 my-0 justify-center">
            <MetricChipHead
              title="Buy / Sell Volume"
              items={[
                {
                  label: "Total Buy",
                  value:
                    analyticsMetrics.buy_sell_metrics["Total Buy (MW)"].value,
                  unit: analyticsMetrics.buy_sell_metrics["Total Buy (MW)"]
                    .unit,
                },
                {
                  label: "Total Sell",
                  value:
                    analyticsMetrics.buy_sell_metrics["Total Sell (MW)"].value,
                  unit: analyticsMetrics.buy_sell_metrics["Total Sell (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              title="Contract Term"
              items={[
                {
                  label: "Long Term",
                  value:
                    analyticsMetrics.contract_term_metrics["Long Term (MW)"]
                      .value,
                  unit: analyticsMetrics.contract_term_metrics["Long Term (MW)"]
                    .unit,
                },
                {
                  label: "Short Term",
                  value:
                    analyticsMetrics.contract_term_metrics["Short Term (MW)"]
                      .value,
                  unit: analyticsMetrics.contract_term_metrics[
                    "Short Term (MW)"
                  ].unit,
                },
              ]}
            />
            <MetricChipHead
              title="Top Buyer & Seller"
              items={[
                {
                  label: `Seller · ${analyticsMetrics.top_buyer_seller_metrics["Top Seller to CSPDCL"].entity}`,
                  value:
                    analyticsMetrics.top_buyer_seller_metrics[
                      "Top Seller to CSPDCL"
                    ].value,
                  unit: analyticsMetrics.top_buyer_seller_metrics[
                    "Top Seller to CSPDCL"
                  ].unit,
                },
                {
                  label: `Buyer · ${analyticsMetrics.top_buyer_seller_metrics["Top Buyer from CSPDCL"].entity}`,
                  value:
                    analyticsMetrics.top_buyer_seller_metrics[
                      "Top Buyer from CSPDCL"
                    ].value,
                  unit: analyticsMetrics.top_buyer_seller_metrics[
                    "Top Buyer from CSPDCL"
                  ].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Generation Overview"
              subtitle="Fetching Chhattisgarh generation data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Volume Drawal ({selectedUnit})
                </h2>
                <div className="flex gap-x-6  justify-end">
                  {selectedGeneratorType.includes("State") &&
                    !selectedGeneratorType.includes("Central") && (
                      <div style={{ minWidth: 160 }}>
                        <SearchBox
                          isMany
                          options={stateGenTypeOptions}
                          selectedOptions={selectedStateGenTypes}
                          onChange={(opts: any[]) =>
                            setSelectedStateGenTypes(opts ?? [])
                          }
                          placeholder="Search generator type"
                        />
                      </div>
                    )}
                  {selectedGroupKeys.label === "Utility" &&
                    (selectedGeneratorType.length === 0 ||
                      selectedGeneratorType.includes("Central")) && (
                      <div style={{ minWidth: 200 }}>
                        <SearchBox
                          isMany
                          options={utilityOptions}
                          selectedOptions={selectedUtilities}
                          onChange={(opts: any[]) =>
                            setSelectedUtilities(opts ?? [])
                          }
                          placeholder="Search utility"
                        />
                      </div>
                    )}
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      const secureTablePar = {
                        ...tablePar,
                        data: chartData || [],
                      };
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(secureTablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(secureTablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(secureTablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {hasNoData ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    minHeight: "400px",
                    fontSize: "18px",
                    color: "#666",
                    fontWeight: "500",
                  }}
                >
                  No transactions found for the selected date and filter
                </div>
              ) : (
                <>
                  {dataTab.label === "Chart" && (
                    <ReMixChart
                      data={chartData}
                      xDataKey={xDataKey}
                      secondXDataKey={secondDataKey()}
                      unit={selectedUnit}
                      yAxisLabel={selectedUnit}
                      isTimeSlot={selectedKey.isTime}
                      showBrush={true}
                      brushStart={BrushStart.End}
                      onlyTitle={true}
                      legends={getActiveLegends()}
                      xTick={
                        selectedKey.isTime && selectedBlock.length === 0
                          ? (value: any) => {
                              return renderHourTick(value, 10);
                            }
                          : undefined
                      }
                      defaultSelectedLegends={selectedLegends}
                      onLegendSelect={handleLegendSelect}
                    />
                  )}
                  {dataTab.label === "Summary" && (
                    <LegendSummaryTable {...tablePar} />
                  )}
                  {dataTab.label === "Table" && <ChartTable {...tablePar} />}
                </>
              )}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div>WBES</div>}
              text="
                1. WBES: OA GNA reports, OA TGNA reports
                2. MW: Represents the average (MW) when the selected time interval is Date. "
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
